/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse, AxiosRequestConfig, ResponseType } from "axios";
import apiFetcher from "../utils/apiFetcher";

type RequestMethod = "POST" | "PUT" | "DELETE" | "PATCH";

interface UsePostDataOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onSettled?: (data: any, error: any) => void;
  responseType?: ResponseType;
}

// Function to determine if a request should receive binary data
function isBinaryEndpoint(url: string): boolean {
  return (
    url.includes("bulk-export") ||
    url.includes("download") ||
    url.includes("export")
  );
}

/**
 * Custom hook to handle data submission using React Query's useMutation
 * @param {string} route - The API endpoint route.
 * @param {RequestMethod} method - HTTP request method (POST, PUT, DELETE, PATCH).
 * @param {UsePostDataOptions} options - Optional handlers for success, error, and settled states.
 * @returns {object} Mutation object containing mutate, isLoading, error, etc.
 */
export const usePostData = <TData = any, TVariables = any>(
  route: string,
  method: RequestMethod = "POST",
  options: UsePostDataOptions = {},
  queryParams: Record<string, any> = {}
) => {
  // Determine if this endpoint requires binary response handling
  const responseType =
    options.responseType || (isBinaryEndpoint(route) ? "blob" : "json");

  return useMutation<AxiosResponse<TData>, any, TVariables>({
    mutationFn: async (payload: TVariables) => {
      const url = queryParams
        ? `${route}?${new URLSearchParams(queryParams)}`
        : route;

      // For binary responses, we need to modify the request config
      const config: AxiosRequestConfig = {
        responseType: responseType,
      };

      switch (method) {
        case "POST":
          return apiFetcher.post(url, payload, config);
        case "PUT":
          return apiFetcher.put(url, payload, config);
        case "DELETE":
          return apiFetcher.delete(url, { data: payload, ...config });
        case "PATCH":
          return apiFetcher.patch(url, payload, config);
        default:
          throw new Error(`Unsupported request method: ${method}`);
      }
    },

    onSuccess: (response) => {
      if (options.onSuccess) options.onSuccess(response);
    },
    onError: (error) => {
      if (options.onError) options.onError(error);
    },
    onSettled: (data, error) => {
      if (options.onSettled) options.onSettled(data, error);
    },
  });
};
